import { Box, Container as ContainerIcon, RotateCw } from "lucide-react";
import type { PodInfo } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle, cn, Tooltip, TooltipContent, TooltipTrigger } from "@fox1ck-org/ui";
import { shortenPod } from "@/features/logs/levelStyles";

interface PodsCardProps {
  pods: PodInfo[];
  onSelectPod?: (podName: string) => void;
}

/**
 * Compact pod overview: each pod is a chip with health/restart count and
 * clicks through to a pre-filtered live tail.
 */
export function PodsCard({ pods, onSelectPod }: PodsCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <Box className="h-4 w-4" />
            Поды
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {pods.filter((p) => p.ready).length} / {pods.length} ready
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {pods.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Запущенных подов нет</p>
        ) : (
          pods.map((pod) => {
            const ready = pod.ready;
            const containers = pod.containers ?? [];
            const readyContainers = containers.filter((c) => c.ready).length;
            const restartCount = pod.restarts;
            return (
              <button
                key={pod.name}
                type="button"
                onClick={() => onSelectPod?.(pod.name)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-md border bg-background/50 text-left hover:bg-accent transition-colors",
                  !ready && "border-yellow-500/40"
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full shrink-0",
                    ready ? "bg-emerald-400" : "bg-yellow-400"
                  )}
                />

                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono truncate text-foreground">
                    {shortenPod(pod.name)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    age {pod.age} · {pod.status}
                  </div>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <ContainerIcon className="h-3 w-3" />
                      {readyContainers}/{containers.length}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="text-xs">
                    <div className="font-mono">{pod.name}</div>
                    <ul className="mt-1 space-y-0.5">
                      {containers.map((c) => (
                        <li key={c.name} className="flex items-center gap-2">
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full",
                              c.ready ? "bg-emerald-400" : "bg-yellow-400"
                            )}
                          />
                          {c.name}
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>

                {restartCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded",
                          restartCount > 5
                            ? "bg-destructive/15 text-destructive"
                            : "bg-yellow-500/15 text-yellow-400"
                        )}
                      >
                        <RotateCw className="h-3 w-3" />
                        {restartCount}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {restartCount} restart{restartCount === 1 ? "" : "s"}
                    </TooltipContent>
                  </Tooltip>
                )}
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
